import {
	FootballContextUtils,
	SportCodes
} from "@prestosports/prestostats-core/Common";

// How the game screens treat overtime (PSTATS-1268). OT Kickoffs plays each
// overtime period like a regulation one; with it off, overtime is a series of
// possessions as before. OT Time Control, a separate setting, decides whether
// overtime is timed in either format, and core marks an untimed overtime with
// fbOtDisabled. Core decides both (isOtKickoffsOn, which in flag football
// also needs Kickoff Play Entry), so these helpers only read them.

const isFlagFootball = (sportId) => sportId === SportCodes.FlagFootball;

/**
 * How the coin toss dialog asks its second question. A toss followed by a
 * kickoff asks which team receives: at game start, unless a flag game has no
 * kickoffs, and in overtime with OT Kickoffs on. Only the game-start answer
 * is stored inverted, as the kicking team, which is how PeriodStart and
 * SecondHalfPossessionSwitch read startSettings.offenseTeam. An overtime toss
 * stores the team that starts on offense, which with a kickoff is the
 * receiving team.
 */
export const getTossQuestion = ({
	overtimePeriod,
	otKickoffs,
	startsWithoutKickoff
}) => {
	const asksReceivingTeam = overtimePeriod ? otKickoffs : !startsWithoutKickoff;
	return {
		asksReceivingTeam,
		storesKickingTeam: !overtimePeriod && asksReceivingTeam
	};
};

/**
 * Whether the crew starts each overtime period by hand (OT+1 in the time
 * dialog). Tackle football always has; series overtime in flag football
 * starts the next period by itself, which OT Kickoffs turns off.
 */
export const crewStartsOvertimePeriods = ({ sportId, gameConfig }) =>
	!isFlagFootball(sportId) || FootballContextUtils.isOtKickoffsOn(gameConfig);

/**
 * Whether the scoreboard's time dialog opens, by a click or by Shift+C: for
 * an event that can still be edited, once the game has started, and in
 * overtime where the crew starts the periods or there is a clock to correct.
 * Flag football's untimed series overtime starts its own periods, so the
 * dialog would have nothing to offer there.
 */
export const canOpenTimeDialog = ({
	canEditCurrentEvent,
	isGameInitialized,
	overtimePeriod,
	fbOtDisabled,
	sportId,
	gameConfig
}) =>
	Boolean(
		canEditCurrentEvent &&
			isGameInitialized &&
			(!overtimePeriod ||
				!fbOtDisabled ||
				crewStartsOvertimePeriods({ sportId, gameConfig }))
	);

/**
 * The allotment the scoreboard's timeout indicator draws against: Timeouts
 * Per Half in regulation, OT Timeouts in overtime, where every period starts a
 * fresh allotment with nothing carried over from regulation (PSTATS-1268).
 */
export const getTimeoutAllotment = ({
	overtimePeriod,
	timeoutsPerHalf,
	otTimeouts
}) => (overtimePeriod ? otTimeouts : timeoutsPerHalf);

/**
 * Whether the scoreboard shows the clock rather than "OT": in regulation, and
 * in overtime while OT Time Control times it.
 */
export const showsGameClock = ({ overtimePeriod, fbOtDisabled }) =>
	!overtimePeriod || !fbOtDisabled;

/**
 * Whether Running Clock's play/pause button and spacebar are offered: wherever
 * the clock is shown. Core keeps the clock paused in an untimed overtime.
 */
export const showsClockControls = ({
	runningClockEnabled,
	overtimePeriod,
	fbOtDisabled
}) =>
	Boolean(
		runningClockEnabled && showsGameClock({ overtimePeriod, fbOtDisabled })
	);
